import { api } from './client';
import { ApiError } from './types';

/**
 * Watching a driver move.
 *
 * ---------------------------------------------------------------------------
 * TWO SPEEDS, AND THE SERVER PICKS
 * ---------------------------------------------------------------------------
 * Ordinarily this is a relaxed poll - somebody watching a car approach does
 * not need a new fix every second, and a phone in a moving vehicle is already
 * spending its battery on the radio.
 *
 * Once an SOS is open on the trip it becomes effectively live. That is not a
 * second transport: it is the same endpoint sampled about five times as often,
 * and the cadence arrives in the response as `pollAfterMs` rather than being
 * decided here. A client that hard-codes its interval cannot be tightened
 * during an incident without shipping a new build, and the moment you would
 * want to tighten it is the moment you cannot.
 *
 * ---------------------------------------------------------------------------
 * WHY LONG-POLLING AND NOT A SOCKET
 * ---------------------------------------------------------------------------
 * This app carries no socket client and no `EventSource`. Adding either for a
 * screen that is open for a few minutes means a native dependency and a
 * reconnect state machine - for a feed that a held HTTP request already
 * delivers, over the connection the app has, surviving the network changes a
 * phone in a car actually goes through.
 *
 * Send back the `version` you last rendered and the server holds the request
 * until something you would draw has changed. Nothing changing is answered
 * with the current view rather than an empty body, so an app returning from
 * the background is resynchronised by the same response that says nothing
 * happened.
 */

/** One reading of where the driver is, as the server keeps it. */
export type RideTracking = {
  rideId: string;
  status: string;
  riderId: string | null;
  position: { lat: number; lng: number } | null;
  /**
   * The driver's phone has stopped reporting.
   *
   * Rendered as a caveat rather than hidden. A frozen pin reads as "they have
   * parked", not "we lost them", and on a moving vehicle that misreading is
   * the dangerous one.
   */
  isStale: boolean;
  lastReportedAt: string | null;
  drop: { lat: number; lng: number; addressText: string | null };
  /** An alert is open on this trip. The screen becomes an incident screen. */
  sosActive: boolean;
  sosAlertId: string | null;
  /** The server's cadence. Honour it; do not substitute your own. */
  pollAfterMs: number;
  /** Echo back as `since` to hold the next request open. */
  version: string;
};

/**
 * The floor on how fast we will ask, whatever the server says.
 *
 * A guard against a bad deploy pinning the phone's radio open, not a tuning
 * knob - the server's emergency cadence sits comfortably above it.
 */
const MIN_POLL_MS = 800;

/** Where to resume from after a failure, so a flapping connection settles. */
const BACKOFF_MS = 4000;

/**
 * One reading, held open until something changes.
 *
 * Resolves `null` rather than throwing when the poll could not be completed -
 * including the client's own request timeout, which on a long poll is the
 * ordinary outcome rather than a fault. `API_TIMEOUT_MS` is shorter than the
 * server's relaxed hold on a local build, so treating that as an error would
 * fill the log with failures describing the feature working as designed.
 *
 * A real error (a 404 on a trip that ended, a 409 on one that is no longer
 * trackable) also resolves null and is reported through `onGone` instead, so
 * the caller can stop rather than hammer.
 */
async function poll(
  rideId: string,
  since: string | undefined,
  signal: AbortSignal,
): Promise<{ view: RideTracking | null; gone: boolean }> {
  try {
    const view = await api.get<RideTracking>(
      `/customers/me/rides/${rideId}/tracking`,
      { query: since ? { since } : undefined, signal },
    );
    return { view, gone: false };
  } catch (error) {
    const status = error instanceof ApiError ? error.status : 0;

    /*
     * The trip is over, or was never ours to watch. Distinct from a network
     * blip: there is nothing to come back for, and retrying would be a loop
     * against a 404. 409 is the server saying the ride is no longer trackable
     * and no alert is open on it - which is the ordinary end of a trip.
     */
    if (status === 404 || status === 409) {
      return { view: null, gone: true };
    }

    // Timeout, abort, offline, 5xx - all the same thing here: ask again.
    return { view: null, gone: false };
  }
}

/**
 * Follow a trip until told to stop.
 *
 * Returns a cancel function. Every emission is a complete view rather than a
 * delta, so a subscriber that misses one - backgrounded, remounted - is
 * correct again on the next.
 *
 * `onGone` fires once when the trip stops being watchable, and the loop ends
 * itself. The caller does not have to police that.
 */
export function watchRide(
  rideId: string,
  handlers: {
    onView: (view: RideTracking) => void;
    onGone?: () => void;
  },
): () => void {
  const controller = new AbortController();
  let stopped = false;
  let since: string | undefined;

  /*
   * Cancellable, and that matters rather than being tidiness.
   *
   * A plain `setTimeout` promise keeps its timer alive after the watcher has
   * been stopped - so leaving the screen mid-wait holds a timer for up to the
   * backoff, and in a test run it holds the whole process open. The loop would
   * exit on the next `stopped` check either way; the timer is the leak.
   */
  let pending: ReturnType<typeof setTimeout> | null = null;
  let wake: (() => void) | null = null;

  const sleep = (ms: number): Promise<void> =>
    new Promise(resolve => {
      wake = resolve;
      pending = setTimeout(() => {
        pending = null;
        wake = null;
        resolve();
      }, ms);
    });

  (async () => {
    while (!stopped) {
      const { view, gone } = await poll(rideId, since, controller.signal);

      if (stopped) return;

      if (gone) {
        handlers.onGone?.();
        return;
      }

      if (view) {
        since = view.version;
        handlers.onView(view);
        /*
         * The server has already held this request for as long as it was going
         * to, so the wait between polls is the cadence it asked for and not a
         * second delay stacked on top of a hold. During an SOS that is about a
         * second and a half.
         */
        await sleep(Math.max(MIN_POLL_MS, view.pollAfterMs));
      } else {
        // Could not reach it. Ease off rather than spin, and drop `since` so
        // the next answer is a full resynchronisation rather than a hold
        // against a version the server may no longer recognise.
        since = undefined;
        await sleep(BACKOFF_MS);
      }
    }
  })();

  return () => {
    stopped = true;
    controller.abort();
    // Cut the wait short rather than letting it run out. The loop's next
    // `stopped` check ends it immediately.
    if (pending) clearTimeout(pending);
    pending = null;
    wake?.();
    wake = null;
  };
}
